import { useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { Me } from "../api/types";
import { hueFor, initialOf } from "../ui";
import { ShellIcon } from "./ShellIcons";
import ThemeSwitch from "./ThemeSwitch";

const ROLE: Record<string, string> = { admin: "管理员", marketing: "市场", agent: "客服" };

export const roleName = (me: Me) => ROLE[me.role] ?? me.role;
export const nameOf = (me: Me) => me.name || me.email.split("@")[0] || me.email;

/** The signed-in person's face: their initial on their own colour, as on the team page; the letter as the prototype's Avatar sets it. */
export function Face({ me, size }: { me: Me; size: number }) {
  return (
    <span className="acct-face" style={{ background: hueFor(nameOf(me)), width: size, height: size, fontSize: Math.round(size * 0.38), fontWeight: 800 }} aria-hidden="true">
      {initialOf(nameOf(me))}
    </span>
  );
}

/**
 * Who is signed in, and the two things that belong to them: how the console
 * looks, and leaving it (prototype v2.15: 外观 lives only here).
 *
 * The button sits at the sidebar's foot — on a phone, at the end of the top
 * bar (compact). The menu is drawn at the page's root, so the sidebar's
 * clipping cannot cut it: above the button when the sidebar is open, beside it
 * when it is folded to a dock, below it on a phone.
 */
export default function AccountMenu({ me, onSignOut, compact }: { me: Me; onSignOut: () => void; compact?: boolean }) {
  const [at, setAt] = useState<CSSProperties | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!at) return;
    const close = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!pop.current?.contains(t) && !btn.current?.contains(t)) setAt(null);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setAt(null);
    // Placed by the button's box: a window that changes size moves the button out from under it.
    const gone = () => setAt(null);
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", esc);
    window.addEventListener("resize", gone);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", esc);
      window.removeEventListener("resize", gone);
    };
  }, [at]);

  const toggle = () => {
    if (at) return setAt(null);
    const b = btn.current;
    if (!b) return;
    const r = b.getBoundingClientRect();
    const docked = !!b.closest(".app.collapsed");
    setAt(
      compact
        ? { top: Math.round(r.bottom + 8), right: Math.round(window.innerWidth - r.right) }
        : docked
          ? { left: Math.round(r.right + 12), bottom: Math.round(window.innerHeight - r.bottom) }
          : { left: Math.round(r.left), bottom: Math.round(window.innerHeight - r.top + 8) },
    );
  };

  const name = nameOf(me);
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`sn-me${compact ? " sm" : ""}`}
        aria-haspopup="menu"
        aria-expanded={!!at}
        aria-label={`账号：${name}`}
        onClick={toggle}
      >
        <Face me={me} size={compact ? 30 : 34} />
        {!compact && (
          <>
            <span className="sn-me-t">
              <b>{name}</b>
              <small>{roleName(me)}</small>
            </span>
            <ShellIcon name="more" className="sn-me-more" />
          </>
        )}
      </button>
      {at &&
        createPortal(
          <div className="acct-pop" role="menu" ref={pop} style={at}>
            <div className="acct-pop-who">
              <Face me={me} size={38} />
              <span>
                <b>{name}</b>
                <small>
                  {me.email} · {roleName(me)}
                </small>
              </span>
            </div>
            <div className="acct-pop-k">外观</div>
            <ThemeSwitch />
            <button
              type="button"
              className="acct-pop-out"
              role="menuitem"
              onClick={() => {
                setAt(null);
                onSignOut();
              }}
            >
              <ShellIcon name="logout" />
              退出登录
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}
